// Public vocabulary of the chart library: tones and series data shapes.
// Everything here is renderer-agnostic — no charting-library types may ever
// appear in this file or in any component's props (ADR 0131).

import type { Threshold } from './lib/series';

export type { SeriesPoint } from './lib/series';

/**
 * A limit drawn on a chart as a labelled line ("Shallow alarm 2.0 m"). The
 * chart line and the alarm red are close hues, the same in Night, so a chart
 * never signals a limit by colour: the line carries a label, and a latest
 * value past it is named in words ("· now below").
 */
export interface ChartThreshold extends Threshold {
  label: string;
}

/**
 * Semantic roles for a mark. The v2 design has one brand hue, so series are
 * told apart by STROKE as well as colour (cloud/docs/web-refresh-v2.md,
 * "Charts"; ADR 0290 Decision 5). Multi-series charts assign series 1, 2, 3 in
 * order and give every series after the third the series-3 style.
 * `good`/`warn`/`bad` are reserved status meanings; use them only when the
 * colour means state, never as "one more series colour".
 */
export type Tone =
  | 'accent' // series 1 — measured history, --chart-line, solid
  | 'series2' // series 2 — --chart-2 (--text-1), dashed 10/4
  | 'series3' // series 3 and later — --chart-3 (--text-3), dotted 1/5
  | 'forecast' // Neomaris's forecast — --text-3, dashed 3/6; no series uses this dash
  | 'neutral' // de-emphasis grey (context marks, pointers), solid
  | 'ink' // text-coloured marks
  | 'good'
  | 'warn'
  | 'bad';

const TONE_VARS: Record<Tone, string> = {
  accent: 'var(--chart-accent)',
  series2: 'var(--chart-series-2)',
  series3: 'var(--chart-series-3)',
  forecast: 'var(--chart-forecast)',
  neutral: 'var(--chart-neutral)',
  ink: 'var(--chart-ink-2)',
  good: 'var(--chart-good)',
  warn: 'var(--chart-warn)',
  bad: 'var(--chart-bad)',
};

/** CSS color expression for a tone (always a theme variable, never a hex). */
export function toneColor(tone: Tone): string {
  return TONE_VARS[tone];
}

/**
 * The colour of the area under a line. The chart line's area takes its kind's
 * own fill colour where a card names a kind (`--chart-area-color`, charts.css,
 * cloud ADR 0396 §3): a kind's line and fill are separate roles. Every other
 * tone fills in its own colour.
 */
export function areaColor(tone: Tone): string {
  return tone === 'accent' ? 'var(--chart-area-color, var(--chart-accent))' : toneColor(tone);
}

/** Series style order for auto-assigned multi-series charts. */
const SERIES_ORDER: Tone[] = ['accent', 'series2', 'series3'];

/** The tone of the `index`th series: 1, 2, then 3 for every series after. */
export function seriesTone(index: number): Tone {
  return SERIES_ORDER[Math.min(index, SERIES_ORDER.length - 1)]!;
}

/** True for the reserved status tones — a numeral may take these, never a series tone. */
export function isStatusTone(tone: Tone | undefined): tone is 'good' | 'warn' | 'bad' {
  return tone === 'good' || tone === 'warn' || tone === 'bad';
}

/** How a line is drawn: colour, width, dash and caps. */
export interface SeriesStroke {
  color: string;
  width: number;
  /** SVG `stroke-dasharray`; undefined draws a solid line. */
  dash: string | undefined;
  cap: 'round' | 'butt';
}

/**
 * iOS `SeriesStyle` and `AirChartStyle` (tomboios `ChartViews.swift`) on the v2
 * palette. Series 1 is solid at `base` px with round caps; series 2 is
 * dashed 10/4, half a pixel thinner, butt caps; series 3 and later are dotted
 * 1/5, a pixel thinner, round caps; the forecast is dashed 3/6 (3/7 wide) at
 * 2.5 px. Every other tone draws solid. `base` is 3 (3.5 on a wide plot).
 */
export function seriesStroke(tone: Tone, base = 3, wide = false): SeriesStroke {
  const color = toneColor(tone);
  switch (tone) {
    case 'series2':
      return { color, width: base - 0.5, dash: '10 4', cap: 'butt' };
    case 'series3':
      return { color, width: base - 1, dash: '1 5', cap: 'round' };
    case 'forecast':
      return { color, width: 2.5, dash: wide ? '3 7' : '3 6', cap: 'butt' };
    default:
      return { color, width: base, dash: undefined, cap: 'round' };
  }
}
